import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import { QueryClient } from "@tanstack/react-query";
import { getGoalStartIndices, getHierarchicalIdentifier, unwrapConnection } from "@utils/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Headline, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  AiPredictionOfEnum,
  ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTreatmentPlanAiGoalSuggestionStatusChoices,
  ApiReportTreatmentPlanAiStgPredictionStatusChoices,
  ApiReportTreatmentPlanAiTargetPredictionStatusChoices,
  ApiTreatmentPlanTemplateAiGenerationConfigChoices,
  ReportDomainType,
  ReportGoalType,
  ReportGoalTypeEdge,
  ReportPageLocationEnums,
  ReportTargetType,
  ReportTargetTypeEdge,
  ReportTypeEnums,
  useAddSuggestedReportGoalsMutation,
  useCreateUpdateReportGoalMutation,
  useCreateUpdateReportTargetMutation,
  useReassignReportGoalMutation,
  useReportGoalsQuery,
  useTreatmentPlanTemplateQuery,
} from "@api/graphql/types-and-hooks";
import { AiFeedbackDialog } from "@components/AiFeedbackDialog/AiFeedbackDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { ConfirmGenerationDialog } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { AddTargetsBank } from "@components/GoalsBank/AddTargetsBank/AddTargetsBankV2";
import { GoalBankShortTermGoalsSaveToFolder } from "@components/GoalsBank/SavedGoals/ShortTermGoals/GoalBankShortTermGoalsSaveToFolder";
import { GoalBankTargetsSaveToFolder } from "@components/GoalsBank/SavedGoals/Targets/GoalBankTargetsSaveToFolder";
import { NoDataCard } from "@components/NoDataCard/NoDataCard";
import { ShortTermGoalCard } from "@components/ShortTermGoalCard/ShortTermGoalCardV2";
import { ReportGoalPanelSingleView } from "@components/ShortTermGoalPanel/ReportGoalPanelSingleView";
import { ShortTermGoalParentCard } from "@components/ShortTermGoalParentCard/ShortTermGoalParentCardV2";
import { TargetCard } from "@components/TargetCard/TargetCardV2";
import { TargetPanelSingleView } from "@components/TargetPanel/TargetPanelSingleView";
import { WarningCard } from "@components/WarningCard/WarningCard";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { GenerationErrorPage } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/GenerationErrorPage";
import { ReportNotReadyYet } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/components/ReportNotReadyYet";
import { BaselineDateBanner, GoalWithDomain } from "@pages/AssessmentReportV2Details/Components/BaselineDateBanner";
import { ManageDomainsDialog } from "@pages/ClinicalReportDetails/ReportPage/Components/ManageDomains/ManageDomainsDialog";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

export const ShortTermGoals: React.FC = () => {
  const { assessmentId } = useParams();
  const go = useNavigate();
  const [queryClient] = useState(() => new QueryClient());
  const { report, reportQuery } = useReport();
  const learnerId = report?.learner?.id || null;
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  const [warnUser, setWarnUser] = useState<boolean>(false);
  const [isTargetPanelOpen, setTargetPanelOpen] = useState<boolean>(false);
  const [selectedDomain, setSelectedDomain] = useState<ReportDomainType>();
  const [selectedGoal, setSelectedGoal] = useState<ReportGoalType | undefined>();
  const [selectedTarget, setSelectedTarget] = useState<ReportTargetType | undefined>();
  const [promptDialogOpen, setPromptDialogOpen] = useState<boolean>(false);
  const [aiFeedbackVote, setAiFeedbackVote] = useState<boolean>(false);
  const [aiFeedbackDialogOpen, setAiFeedbackDialogOpen] = useState<boolean>(false);
  const [selectedAiGoalId, setSelectedAiGoalId] = useState<string | null>(null);
  const addSuggestedGoalsMutation = useAddSuggestedReportGoalsMutation({});
  const reassignReportGoalMutation = useReassignReportGoalMutation({});
  const createGoalMutation = useCreateUpdateReportGoalMutation({});
  const createTargetMutation = useCreateUpdateReportTargetMutation({});
  const [showShortTermGoalsSaveToFolderDialog, setShowShortTermGoalsSaveToFolderDialog] = useState<boolean>(false);
  const [showTargetsSaveToFolderDialog, setShowTargetsSaveToFolderDialog] = useState<boolean>(false);
  const [isManageDomainsOpen, setIsManageDomainsOpen] = useState<boolean>(false);
  const { roles } = useAdminData();
  const isFronteraInternal = roles.includes("frontera-internal");
  // Lifted out of BaselineDateBanner so both creation paths (manual + AI) can
  // pre-fill new goals with the user's selected date.
  const [bannerDate, setBannerDate] = useState<string>("");

  const { refetch: refetchAssessmentReport } = reportQuery;

  const reportTreatmentPlan = report.reportTreatmentPlan;

  const stgStatus = reportTreatmentPlan?.aiStgPredictionStatus;
  const targetStatus = reportTreatmentPlan?.aiTargetPredictionStatus;
  const suggestionStatus = reportTreatmentPlan?.aiGoalSuggestionStatus;

  const suggestionDone =
    suggestionStatus === ApiReportTreatmentPlanAiGoalSuggestionStatusChoices.GoalPredictionPredicted ||
    suggestionStatus === ApiReportTreatmentPlanAiGoalSuggestionStatusChoices.GoalPredictionSkipped ||
    suggestionStatus === ApiReportTreatmentPlanAiGoalSuggestionStatusChoices.Failure ||
    suggestionStatus === ApiReportTreatmentPlanAiGoalSuggestionStatusChoices.Pending ||
    suggestionStatus === undefined;

  const reportStillGenerating =
    !(
      stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPredicted ||
      stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionSkipped
    ) ||
    !(
      targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPredicted ||
      targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionSkipped
    ) ||
    !suggestionDone;

  // Refetch assessment report every 3 seconds when still generating
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (reportStillGenerating) {
      pollInterval = setInterval(async () => {
        await refetchAssessmentReport();
      }, config.REPORT_POLLING_INTERVAL || 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [reportStillGenerating, refetchAssessmentReport]);

  const reportDone =
    (stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPredicted ||
      stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionSkipped) &&
    (targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPredicted ||
      targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionSkipped);

  const reportNotYetKickedOff =
    stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Pending &&
    targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Pending &&
    (suggestionStatus === ApiReportTreatmentPlanAiGoalSuggestionStatusChoices.Pending ||
      suggestionStatus === undefined);

  const treatmentPlanTemplateQuery = useTreatmentPlanTemplateQuery(
    {
      reportId: assessmentId ? assessmentId : "<missing-assessment-id>",
    },
    {
      enabled: !!assessmentId,
      retry: false,
    },
  );
  const treatmentPlanTemplate = treatmentPlanTemplateQuery?.data?.treatmentPlanTemplate;
  const isAddTargetsEnabled = treatmentPlanTemplate?.isAddTargetsEnabled ?? true; // Default to true if undefined
  const isGasEnabled = treatmentPlanTemplate?.isGasEnabled || false;
  const isMedicalNecessityEnabled = treatmentPlanTemplate?.isMedicalNecessityEnabled ?? true;
  const isGoalBankSuggestionsEnabled = treatmentPlanTemplate?.isGoalBankSuggestionsEnabled ?? false;
  const aiTargetsDisabled =
    treatmentPlanTemplate?.aiGenerationConfig == ApiTreatmentPlanTemplateAiGenerationConfigChoices.GoalsOnly;
  const reportGenerationFailed = stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Failure;
  const reportGenerationFailureReason = reportTreatmentPlan?.failureReason;

  const reportGoalsQuery = useReportGoalsQuery(
    {
      id: assessmentId ? assessmentId : "<missing-assessmentId>",
      filter: {
        reportType: ReportTypeEnums.InitialAssessment,
      },
    },
    {
      queryKey: ["get-report-goals-query", learnerId, assessmentId, reportDone],
      enabled: !!assessmentId && !!learnerId && reportDone,
      retry: false,
      refetchOnMount: reportStillGenerating ? false : "always",
      refetchOnWindowFocus: false,
    },
  );

  const { data, error, refetch, isSuccess } = reportGoalsQuery;

  // Refetch goals when generation finishes so suggested goals appear without a hard refresh
  const prevStillGenerating = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevStillGenerating.current === true && !reportStillGenerating) {
      refetch();
    }
    prevStillGenerating.current = reportStillGenerating;
  }, [reportStillGenerating, refetch]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    // Check if any goal is currently generating GAS
    const domains = data?.getReports?.edges?.[0]?.node?.reportTreatmentPlan?.reportdomainSet?.edges || [];
    const hasGeneratingGas = domains.some((domain) =>
      domain?.node?.reportgoalSet?.edges?.some(
        (stg) =>
          stg?.node?.aiGoalAttainmentScalePredictionStatus ===
          ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPending,
      ),
    );

    if (hasGeneratingGas) {
      pollInterval = setInterval(async () => {
        await refetch();
      }, config.REPORT_POLLING_INTERVAL || 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [data, refetch]);

  useEffect(() => {
    if (selectedGoal && data) {
      // Find the updated version of the selected goal in the fresh data
      const domains = data?.getReports?.edges?.[0]?.node?.reportTreatmentPlan?.reportdomainSet?.edges || [];
      const updatedGoal = domains
        .flatMap((domain) => domain?.node?.reportgoalSet?.edges || [])
        .find((reportGoal) => reportGoal?.node?.id === selectedGoal.id);

      if (updatedGoal?.node) {
        setSelectedGoal(updatedGoal.node as ReportGoalType);
      }
    }
  }, [data, selectedGoal?.id]);

  const goalCategoryName = "Domain";

  const domainList = data?.getReports?.edges?.[0]?.node?.reportTreatmentPlan?.reportdomainSet?.edges || [];

  // Every in-use goal across all domains — what the bulk baseline-date banner
  // targets. The reportGoals query doesn't select `reportDomain` per goal, so
  // tag each goal with its parent domain id here (createUpdateReportGoal needs
  // it on every call).
  const allInUseGoals: GoalWithDomain[] = domainList.flatMap((domain) => {
    const domainId = domain?.node?.id;
    if (!domainId) return [];
    return (unwrapConnection(domain?.node?.reportgoalSet) ?? [])
      .filter((goal): goal is ReportGoalType => !!goal?.inUse)
      .map((goal) => ({ goal, domainId }));
  });

  // Goals are numbered continuously across domains (1..N, not restarting per
  // domain). Precompute each domain's starting goal number from the in-use goal
  // counts of the domains before it — mirrors the `goalsInUse` filter below.
  const domainGoalOffsets = getGoalStartIndices(
    domainList.map((domain) => (unwrapConnection(domain?.node?.reportgoalSet) || []).filter((g) => g?.inUse).length),
  );

  const flatGoalSet = data?.getReports?.edges?.[0]?.node?.reportTreatmentPlan?.reportgoalSet;
  const goalsForManageDialog = useMemo<ReportGoalType[]>(
    () => (unwrapConnection(flatGoalSet) ?? []).filter((goal): goal is ReportGoalType => !!goal),
    [flatGoalSet],
  );

  useEffect(() => {
    if (error) {
      console.error("Error while fetching report goal list", error);
      notifyError(`Error fetching ${goalCategoryName}`);
    }
  }, [error]);

  const handleReassignReportGoalMutation = async (newReportDomainId: string, reportGoalId: string) => {
    await reassignReportGoalMutation
      .mutateAsync({
        input: { reportGoalId: reportGoalId, newReportDomainId: newReportDomainId },
      })
      .then((data) => {
        if (data?.reassignReportGoal?.status) {
          notifySuccess(data.reassignReportGoal.message ?? "");
          refetch();
        } else {
          notifyError(data.reassignReportGoal?.message ?? "Failed to reassign goal");
        }
      })
      .catch((error) => {
        console.error("Error reassign goal : ", error);
      });
  };

  const getReassignableGoals = (reportDomainId: string, reportGoalId: string) =>
    domainList
      .filter((item) => item?.node?.id !== reportDomainId)
      .map((item) => ({
        label: item?.node?.name ?? "",
        callback: () => handleReassignReportGoalMutation(item?.node?.id ?? "<missing-report-domain-id>", reportGoalId),
      })) || [];

  if (reportNotYetKickedOff) {
    return <ReportNotReadyYet item="Short Term Goals" />;
  }

  if (reportGenerationFailed) {
    return <GenerationErrorPage page={AiPredictionOfEnum.ShortTermGoal} errorMessage={reportGenerationFailureReason} />;
  }

  if (reportStillGenerating) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center gap-y-4">
        <div className="flex flex-col items-center justify-center gap-y-2">
          <Loader className="m-0 h-6 w-6 text-limestone-500 animate-spin" />
          <Headline size="small">Your goal suggestions are being generated</Headline>
        </div>
        <Paragraph displayType="normal" colorType="secondary" className="text-center">
          This may take a moment <br />
          You can leave this page and come back later
        </Paragraph>
        <Button
          appearance="primary"
          text="Return to Assessments"
          onClick={() => {
            go("/assessment-reports");
          }}
        />
      </div>
    );
  }

  return (
    <>
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.ReportPart_2}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />
      <GoalBankShortTermGoalsSaveToFolder
        key={selectedGoal?.id}
        open={showShortTermGoalsSaveToFolderDialog}
        aiShortTermGoals={[]}
        shortTermGoals={[selectedGoal as ReportGoalType]}
        onCloseClick={() => {
          setSelectedGoal(undefined);
          setShowShortTermGoalsSaveToFolderDialog(false);
        }}
        refetch={async () => await queryClient.refetchQueries()}
      />
      <GoalBankTargetsSaveToFolder
        key={selectedTarget?.id}
        open={showTargetsSaveToFolderDialog}
        aiTargets={[]}
        targets={[selectedTarget as ReportTargetType]}
        onCloseClick={() => {
          setSelectedTarget(undefined);
          setShowTargetsSaveToFolderDialog(false);
        }}
        refetch={async () => await queryClient.refetchQueries()}
      />
      <div className="h-full px-14 py-4">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row gap-2 items-center">
            <Headline size="medium">Short Term Goals</Headline>
            <Tooltip text="Short Term Goals are the incremental steps toward achieving the Long Term Goals. They are more immediate objectives that can be attained in a shorter time frame."></Tooltip>
          </div>
          {isFronteraInternal && (
            <Button
              text="Manage domains"
              variant="secondary"
              buttonStyle="border"
              size="sm"
              onClick={() => setIsManageDomainsOpen(true)}
            />
          )}
        </div>

        <BaselineDateBanner
          className="mt-4"
          value={bannerDate}
          onChange={setBannerDate}
          goals={allInUseGoals}
          reportId={assessmentId ?? "<missing-assessment-id>"}
          learnerId={learnerId ?? "<missing-learner-id>"}
          refetch={refetch}
        />

        <div className="mt-4">
          {domainList.length === 0 && isSuccess ? (
            <NoDataCard
              title={`You have no ${goalCategoryName}s added`}
              description={
                <span>
                  {goalCategoryName}s that you add will end up here. <br />
                  Add a {goalCategoryName} from our suggestions, or create one of your own to get started
                </span>
              }
            >
              {warnUser && (
                <WarningCard content={`You must have at least one ${goalCategoryName} in your treatment plan`} />
              )}
            </NoDataCard>
          ) : (
            <></>
          )}
          {domainList.map((domain, domainIndex) => {
            const aiGoalList = domain?.node?.reportgoalSet?.edges?.filter((g) => g?.node?.aiGenerated) || [];
            const goalsInUse = domain?.node?.reportgoalSet?.edges.filter((g) => g?.node?.inUse) || [];
            const goalNumberOffset = domainGoalOffsets[domainIndex];
            return (
              <ShortTermGoalParentCard
                key={domain?.node?.id}
                learnerId={learnerId ?? "<missing-learner-id>"}
                reportId={assessmentId}
                reportDomainId={domain?.node?.id}
                refetch={refetch}
                title={domain?.node?.name || "N/A"}
                isGoalBankSuggestionsEnabled={isGoalBankSuggestionsEnabled}
                aiGenerationConfig={treatmentPlanTemplate?.aiGenerationConfig}
                aiGoalList={aiGoalList as ReportGoalTypeEdge[]}
                aiGoals={aiGoalList
                  ?.sort((_, gb) => (gb?.node?.inUse ? 1 : -1))
                  ?.map((goal) => ({
                    body: goal?.node?.description || "",
                    id: goal?.node?.id || "",
                    subtitle: "",
                    title: goal?.node?.goalName || "",
                  }))}
                aiGoalsBeingUsed={aiGoalList.filter((g) => g?.node?.inUse)?.map((g) => g?.node?.id) as string[]}
                addAiGoals={async (goals) => {
                  try {
                    await addSuggestedGoalsMutation.mutateAsync({
                      reportId: assessmentId || "<missing-report-id>",
                      aiSuggestedGoalIds: goals.map((goal) => goal.id),
                    });
                    await refetch();
                  } catch (err) {
                    console.error("Error adding AI goals", err);
                    throw new Error(String(err));
                  }
                }}
                subtitle={domain?.node?.description ?? ""}
                onButtonClick={async () => {
                  setSelectedDomain(domain?.node as ReportDomainType);
                  setSelectedTarget(undefined);
                  try {
                    const response = await createGoalMutation.mutateAsync({
                      goalInput: {
                        reportId: assessmentId || "<missing-report-id>",
                        reportDomainId: domain?.node?.id || "<missing-domain-id>",
                        // Inherit the banner's selected date so the user
                        // doesn't have to re-apply after creating each goal.
                        ...(bannerDate ? { baselineDate: bannerDate } : {}),
                      },
                      learnerId: learnerId || "<missing-learner-id>",
                    });
                    if (!response.createUpdateReportGoal?.status) {
                      notifyError(response.createUpdateReportGoal?.message || "Failed to create new goal");
                      return;
                    }
                    const createdGoal = response.createUpdateReportGoal.reportGoalData;
                    if (createdGoal) {
                      setSelectedGoal(createdGoal as ReportGoalType);
                      setPanelOpen(true);
                      await refetch();
                    }
                  } catch (error) {
                    console.error("Error creating new goal:", error);
                    notifyError("Failed to create new goal");
                  }
                }}
                onAiGoalUpDownVote={async (goal, vote) => {
                  datadogRum.addAction("ai-vote", {
                    type: "stg",
                    direction: vote ? "up-vote" : "down-vote",
                    field: goal.title,
                  });
                  setAiFeedbackVote(vote);
                  setSelectedAiGoalId(goal.id);
                  setAiFeedbackDialogOpen(true);
                }}
              >
                <>
                  {goalsInUse.length ? (
                    <>
                      <div className="grid snap-x grid-cols-3 gap-2 rounded-md">
                        {goalsInUse.map((goal, stgIndex) => {
                          const targetsInUse = goal?.node?.reporttargetSet?.edges?.filter((t) => t?.node?.inUse) || [];
                          const aiTargets =
                            goal?.node?.reporttargetSet?.edges?.filter((t) => t?.node?.aiGenerated) || [];
                          const nonAiTargets =
                            goal?.node?.reporttargetSet?.edges?.filter((t) => !!t?.node?.inUse) || [];
                          return (
                            <ShortTermGoalCard
                              uiGoalId={getHierarchicalIdentifier(goalNumberOffset + stgIndex)}
                              onSaveToFolder={() => {
                                setSelectedDomain(domain?.node as ReportDomainType);
                                setSelectedGoal(goal?.node as ReportGoalType);
                                setShowShortTermGoalsSaveToFolderDialog(true);
                              }}
                              key={goal?.node?.id}
                              goalObject={goal?.node as ReportGoalType}
                              refetchGoals={async () => {
                                await refetch();
                              }}
                              onClick={() => {
                                setSelectedDomain(domain?.node as ReportDomainType);
                                setSelectedGoal(goal?.node as ReportGoalType);
                                setPanelOpen(true);
                              }}
                              submenuGoalsList={getReassignableGoals(
                                domain?.node?.id ?? "<missing-report-domain-id>",
                                goal?.node?.id ?? "<missing-report-goal-id>",
                              )}
                              submenuGoalsOpenDirection={(stgIndex + 1) % 3 === 0 ? "left" : "right"}
                            >
                              <div className="flex flex-start flex-col h-full overflow-y-auto">
                                <div className="flex flex-row gap-2 mb-4">
                                  {isAddTargetsEnabled && (
                                    <>
                                      <AddTargetsBank
                                        reportType={ApiReportReportTypeChoices.InitialAssessment}
                                        key={goal?.node?.id}
                                        learnerId={learnerId ?? "<missing-learner-id>"}
                                        reportGoalId={goal?.node?.id ?? ""}
                                        shortTermGoalBankId={goal?.node?.goalBank?.id}
                                        assessmentId={assessmentId ?? "<missing-assessment-id>"}
                                        onAdd={async () => {
                                          await refetch();
                                        }}
                                        targets={nonAiTargets as ReportTargetTypeEdge[]}
                                        aiTargets={aiTargets as ReportTargetTypeEdge[]}
                                        hideAiTargets={aiTargetsDisabled}
                                        className="w-full"
                                      />
                                      <Button
                                        text="+ Custom Target"
                                        buttonStyle="filled"
                                        variant="secondary"
                                        buttonAction="action"
                                        className="w-full"
                                        onClick={async (e) => {
                                          e?.stopPropagation();
                                          setSelectedGoal(goal?.node as ReportGoalType);
                                          try {
                                            const response = await createTargetMutation.mutateAsync({
                                              targetData: {
                                                reportGoalId: goal?.node?.id || "<missing-goal-id>",
                                                reportId: assessmentId || "<missing-report-id>",
                                              },
                                              learnerId: learnerId || "<missing-learner-id>",
                                            });
                                            if (!response.createUpdateReportTarget?.status) {
                                              notifyError(
                                                response.createUpdateReportTarget?.message ||
                                                  "Failed to create new target",
                                              );
                                              return;
                                            }
                                            const createdTarget = response.createUpdateReportTarget.reportTargetDetails;
                                            if (createdTarget) {
                                              await refetch();
                                              setSelectedTarget(createdTarget as ReportTargetType);
                                              setTargetPanelOpen(true);
                                            }
                                          } catch (error) {
                                            console.error("Error creating new target:", error);
                                            notifyError("Failed to create new target");
                                          }
                                        }}
                                      />
                                    </>
                                  )}
                                </div>

                                <div>
                                  {targetsInUse.length ? (
                                    <>
                                      {targetsInUse.map((target, targetIndex) => {
                                        return (
                                          <TargetCard
                                            onSaveToFolder={() => {
                                              setSelectedTarget(target?.node as ReportTargetType);
                                              setShowTargetsSaveToFolderDialog(true);
                                            }}
                                            uiTargetId={getHierarchicalIdentifier(
                                              goalNumberOffset + stgIndex,
                                              targetIndex,
                                            )}
                                            key={target?.node?.id}
                                            title={target?.node?.targetName || ""}
                                            targetId={target?.node?.id || "<missing-target-id>"}
                                            refetchGoals={async () => {
                                              await refetch();
                                            }}
                                            onClick={() => {
                                              setSelectedGoal(goal?.node as ReportGoalType);
                                              setSelectedTarget(target?.node as ReportTargetType);
                                              setTargetPanelOpen(true);
                                            }}
                                          />
                                        );
                                      })}
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            </ShortTermGoalCard>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <NoDataCard
                      title="You have no goals added"
                      description={
                        <span>
                          Goals that you add will end up here. <br />
                          Add a goal from our suggestions, or create one of your own to get started
                        </span>
                      }
                    >
                      {warnUser && (
                        <WarningCard content="You must have at least one goal per domain in your treatment plan" />
                      )}
                    </NoDataCard>
                  )}
                </>
              </ShortTermGoalParentCard>
            );
          })}

          <ReportGoalPanelSingleView
            title={selectedGoal ? "Edit Report Goal" : "Add Report Goal"}
            isPanelOpen={isPanelOpen}
            setPanelOpen={setPanelOpen}
            selectedGoal={selectedGoal}
            refetch={refetch}
            reportDomain={selectedDomain}
            reportQuery={reportQuery}
            isGasEnabled={isGasEnabled}
            isMedicalNecessityEnabled={isMedicalNecessityEnabled}
          />

          <TargetPanelSingleView
            title={selectedTarget ? "Edit Target" : "Add Target"}
            isTargetPanelOpen={isTargetPanelOpen}
            setTargetPanelOpen={setTargetPanelOpen}
            shortTermGoal={selectedGoal}
            selectedTarget={selectedTarget}
            refetch={refetch}
            reportQuery={reportQuery}
          />

          <AiFeedbackDialog
            isDialogOpen={aiFeedbackDialogOpen}
            setDialogOpen={setAiFeedbackDialogOpen}
            positiveFeedback={aiFeedbackVote}
            objectType="stg"
            objectId={selectedAiGoalId}
          />
        </div>
        <FooterButtonRow
          primaryButtonAction={async () => {
            setWarnUser(false);
            setPromptDialogOpen(true);
            return true;
          }}
          skipNavigate={true}
        />
        {isFronteraInternal && (
          <ManageDomainsDialog
            open={isManageDomainsOpen}
            onClose={() => setIsManageDomainsOpen(false)}
            reportId={assessmentId ?? ""}
            goalList={goalsForManageDialog}
            onSuccess={async () => {
              await Promise.all([refetch(), refetchAssessmentReport()]);
            }}
          />
        )}
      </div>
    </>
  );
};
